import type { AssetRopaDpiaFilterFields } from 'config/api/assetRopa/assetRopaDpiaFilters';
import { DPIA_LIFECYCLE_STATUS_FILTER_VALUES, DPIA_REQUEST_STATUS_FILTER_VALUES } from 'config/api/assetRopa/assetRopaDpiaFilters';
import processing_activities_messages from 'messages/processing_activities_messages';

export type ApaDpiaDrawerFilterChip = {
  id: string;
  name: string | string[];
};

export type ApaDpiaDrawerFilterSection = {
  sectionName: string[];
  filters: ApaDpiaDrawerFilterChip[];
  isSingleSelect: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string[];
};

export type DpiaCombinedStatusFilterId = `request:${string}` | `lifecycle:${string}`;

export type DpiaCombinedStatusOption = {
  id: DpiaCombinedStatusFilterId;
  name: string | string[];
};

const REQUEST_STATUS_PREFIX = 'request:';
const LIFECYCLE_STATUS_PREFIX = 'lifecycle:';

const booleanFilterMessage = (value: 'true' | 'false'): string | string[] =>
  processing_activities_messages.asset_ropa.filters[value === 'true' ? 'yes' : 'no'];

const requestStatusMessage = (value: string): string | string[] => {
  if (value === 'indicated') {
    return processing_activities_messages.asset_ropa.dpia_status.indicated;
  }

  if (value === 'draft') {
    return processing_activities_messages.asset_ropa.dpia_status.draft_ra;
  }

  const messageMap = processing_activities_messages.asset_ropa.dpia_request_status as Record<string, string[]>;
  return messageMap[value] ?? value;
};

const lifecycleStatusMessage = (value: string): string | string[] => {
  const messageMap = processing_activities_messages.asset_ropa.dpia_status as Record<string, string[]>;
  return messageMap[value] ?? value;
};

export const buildDpiaRequestFilterId = (value: string): DpiaCombinedStatusFilterId => `${REQUEST_STATUS_PREFIX}${value}`;

export const buildDpiaLifecycleFilterId = (value: string): DpiaCombinedStatusFilterId => `${LIFECYCLE_STATUS_PREFIX}${value}`;

export const DPIA_COMBINED_STATUS_OPTIONS: DpiaCombinedStatusOption[] = [
  ...DPIA_REQUEST_STATUS_FILTER_VALUES.map(value => ({
    id: buildDpiaRequestFilterId(value),
    name: requestStatusMessage(value),
  })),
  ...DPIA_LIFECYCLE_STATUS_FILTER_VALUES.map(value => ({
    id: buildDpiaLifecycleFilterId(value),
    name: lifecycleStatusMessage(value),
  })),
];

const parseCombinedStatusFilterId = (id: string): { kind: 'request' | 'lifecycle'; value: string } | null => {
  if (id.startsWith(REQUEST_STATUS_PREFIX)) {
    return { kind: 'request', value: id.slice(REQUEST_STATUS_PREFIX.length) };
  }

  if (id.startsWith(LIFECYCLE_STATUS_PREFIX)) {
    return { kind: 'lifecycle', value: id.slice(LIFECYCLE_STATUS_PREFIX.length) };
  }

  return null;
};

const combinedStatusMessage = (id: string): string | string[] => {
  const parsed = parseCombinedStatusFilterId(id);
  if (!parsed) {
    return id;
  }

  if (parsed.kind === 'request') {
    return requestStatusMessage(parsed.value);
  }

  return lifecycleStatusMessage(parsed.value);
};

export const buildDpiaIndicationDrawerSection = (): ApaDpiaDrawerFilterSection => ({
  sectionName: processing_activities_messages.asset_ropa.filters.dpia_indication,
  filters: (['true', 'false'] as const).map(value => ({
    id: value,
    name: booleanFilterMessage(value),
  })),
  isSingleSelect: true,
});

export const buildDpiaCombinedStatusDrawerSection = (): ApaDpiaDrawerFilterSection => ({
  sectionName: processing_activities_messages.asset_ropa.filters.dpia_status_lifecycle,
  filters: DPIA_COMBINED_STATUS_OPTIONS,
  isSingleSelect: true,
});

export const readDpiaIndicationFromTableFilters = (tableFilters: {
  dpia_indication?: ApaDpiaDrawerFilterChip[];
}): AssetRopaDpiaFilterFields['dpiaIndication'] =>
  (tableFilters.dpia_indication?.[0]?.id as AssetRopaDpiaFilterFields['dpiaIndication']) ?? '';

const readCombinedStatusChip = (chip?: ApaDpiaDrawerFilterChip): Pick<AssetRopaDpiaFilterFields, 'dpiaRequestStatus' | 'dpiaStatus'> => {
  if (!chip) {
    return { dpiaRequestStatus: '', dpiaStatus: '' };
  }

  const parsed = parseCombinedStatusFilterId(chip.id);
  if (!parsed) {
    return { dpiaRequestStatus: '', dpiaStatus: '' };
  }

  if (parsed.kind === 'request') {
    return {
      dpiaRequestStatus: parsed.value as AssetRopaDpiaFilterFields['dpiaRequestStatus'],
      dpiaStatus: '',
    };
  }

  return {
    dpiaRequestStatus: '',
    dpiaStatus: parsed.value as AssetRopaDpiaFilterFields['dpiaStatus'],
  };
};

export const readDpiaFiltersFromTableSections = (tableFilters: {
  dpia_indication?: ApaDpiaDrawerFilterChip[];
  dpia_request?: ApaDpiaDrawerFilterChip[];
  dpia_status?: ApaDpiaDrawerFilterChip[];
}): AssetRopaDpiaFilterFields => {
  const dpiaIndication = readDpiaIndicationFromTableFilters(tableFilters);
  const combinedStatus = readCombinedStatusChip(tableFilters.dpia_status?.[0]);

  if (combinedStatus.dpiaRequestStatus || combinedStatus.dpiaStatus) {
    return {
      dpiaIndication,
      ...combinedStatus,
    };
  }

  const legacyRequestStatus = tableFilters.dpia_request?.[0]?.id;
  if (legacyRequestStatus) {
    return {
      dpiaIndication,
      dpiaRequestStatus: legacyRequestStatus as AssetRopaDpiaFilterFields['dpiaRequestStatus'],
      dpiaStatus: '',
    };
  }

  return {
    dpiaIndication,
    dpiaRequestStatus: '',
    dpiaStatus: '',
  };
};

export const writeDpiaFiltersToTableSections = (
  filters: AssetRopaDpiaFilterFields,
): {
  dpia_indication?: ApaDpiaDrawerFilterChip[];
  dpia_status?: ApaDpiaDrawerFilterChip[];
} => {
  const tableFilters: {
    dpia_indication?: ApaDpiaDrawerFilterChip[];
    dpia_status?: ApaDpiaDrawerFilterChip[];
  } = {};

  if (filters.dpiaIndication) {
    tableFilters.dpia_indication = [
      {
        id: filters.dpiaIndication,
        name: booleanFilterMessage(filters.dpiaIndication as 'true' | 'false'),
      },
    ];
  }

  if (filters.dpiaRequestStatus) {
    tableFilters.dpia_status = [
      {
        id: buildDpiaRequestFilterId(filters.dpiaRequestStatus),
        name: combinedStatusMessage(buildDpiaRequestFilterId(filters.dpiaRequestStatus)),
      },
    ];
  } else if (filters.dpiaStatus) {
    tableFilters.dpia_status = [
      {
        id: buildDpiaLifecycleFilterId(filters.dpiaStatus),
        name: combinedStatusMessage(buildDpiaLifecycleFilterId(filters.dpiaStatus)),
      },
    ];
  }

  return tableFilters;
};

export const applyCombinedDpiaStatusSelection = <T extends AssetRopaDpiaFilterFields>(filters: T, combinedStatusId: string): T => {
  const parsed = parseCombinedStatusFilterId(combinedStatusId);
  if (!parsed) {
    return {
      ...filters,
      dpiaRequestStatus: '',
      dpiaStatus: '',
    };
  }

  if (parsed.kind === 'request') {
    return {
      ...filters,
      dpiaRequestStatus: parsed.value as AssetRopaDpiaFilterFields['dpiaRequestStatus'],
      dpiaStatus: '',
    };
  }

  return {
    ...filters,
    dpiaRequestStatus: '',
    dpiaStatus: parsed.value as AssetRopaDpiaFilterFields['dpiaStatus'],
  };
};

export const getCombinedDpiaStatusFilterValue = (filters: AssetRopaDpiaFilterFields): string => {
  if (filters.dpiaRequestStatus) {
    return buildDpiaRequestFilterId(filters.dpiaRequestStatus);
  }

  if (filters.dpiaStatus) {
    return buildDpiaLifecycleFilterId(filters.dpiaStatus);
  }

  return '';
};
